import { Injectable } from "@angular/core";
import { IPreference } from "../model/dialog-state.model";
import { Capability, RoamingSettingKey } from "../model/shared.model";
import { SharedService } from "./shared.service";
import { NamedPreference, SoApiService } from "./soapi.service";
import { OutlookService } from "./outlook/outlook.service";
import { UserInfoService } from "./userinfo.service";
import { MonitoringService } from "./monitoring.service";

const SuperOfficeOwnTenant = "cust1990";

@Injectable({
  providedIn: "root",
})
export class PreferenceService {
  private isInitialized: boolean = false;
  private prefs: IPreference[];
  private hasLoggedMissingTenant = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly outlookService: OutlookService,
    private readonly so: SoApiService,
    private readonly userInfoService: UserInfoService,
    private readonly monitoringService: MonitoringService
  ) {
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // are things missing in the SharedService preference cache?
    // populate!
    if (!this.sharedService.currentCapabilitiesPrivileged
      || !this.sharedService.currentPreferencesDialogState) {

      const [prefs, caps, namedPrefs] = await Promise.all([
        this.so.getPreferencesDialogstate(),
        this.so.getCapabilitiesPrivileged(),
        this.so.getNamedPreferences()
      ]);

      // map named preferences from agent into same array we got from dialogstate
      prefs.Preferences = prefs.Preferences.concat(namedPrefs.map(this.mapNamedPreferenceToPreference));

      this.sharedService.currentPreferencesDialogState = prefs;
      this.sharedService.currentCapabilitiesPrivileged = caps;

      this.outlookService.roamingSettings.set(RoamingSettingKey.SmartAlertsEnabled, this.sharedService.hasCapability(Capability.SofoSmartAlerts));
      this.outlookService.roamingSettings.saveAsync();
    }

    // setup local state from global cache
    this.prefs = this.sharedService.currentPreferencesDialogState.Preferences;
    this.isInitialized = true;

    // load outlook service preferences
    if (!this.outlookService.currentCapabilitiesPrivileged
      || !this.outlookService.currentPreferencesDialogState) {

      const [prefs, caps, namedPrefs] = await Promise.all([
        this.so.getPreferencesDialogstate(),
        this.so.getCapabilitiesPrivileged(),
        this.so.getNamedPreferences()
      ]);

      prefs.Preferences = prefs.Preferences.concat(namedPrefs.map(this.mapNamedPreferenceToPreference));

      this.outlookService.currentPreferencesDialogState = prefs;
      this.outlookService.currentCapabilitiesPrivileged = caps;
    }

    // setup local state from global cache
    this.prefs = this.outlookService.currentPreferencesDialogState.Preferences;
    this.isInitialized = true;

    this.logCategoryTaggingAdoption();
  }

  logCategoryTaggingAdoption(): void {
    if (!this.isCategoryTaggingFeatureEnabled || !this.userInfoService.tenant) {
      return;
    }

    const enabled = this.isCategoryTaggingEnabled;
    this.monitoringService.logCustomEvent("CategoryTagging: enabled state", {
      tenant: this.userInfoService.tenant,
      associateId: this.userInfoService.associate_id,
      enabled,
      ...(enabled && {
        onOpen: this.enableCategoryOnOpen,
        onSendAndArchive: this.enableCategoryOnSendAndArchive,
        forSharedMailbox: this.enableCategoryForSharedMailbox,
      }),
    });
  }

  async savePreference(keyName: string, value: string): Promise<void> {
    const previousValue = this.getStringPreference(keyName);
    this.setStringPreference(keyName, value);

    try {
      await this.so.savePreferences([{ Key: keyName, Value: value }]);
    } catch (e) {
      // A newer save may have replaced the value meanwhile; only undo our own.
      if (this.getStringPreference(keyName) === value) {
        this.setStringPreference(keyName, previousValue);
      }
      throw e;
    }
  }

  /* useful capabilities as getter-only "preferences" */

  get supportsPreferenceDialog(): boolean {
    // this capability is what triggered the "need" for a preference dialog in SOFO
    return this.sharedService.hasCapability(Capability.SupportsContextualSalesAndProjectsForContact);
  }

  get isCategoryTaggingFeatureEnabled(): boolean {
    return this.sharedService.hasCapability(Capability.SofoEmailCategory);
  }

  get categoryTaggingOnByDefault(): boolean {
    const tenant = this.userInfoService.tenant;
    if (!tenant && !this.hasLoggedMissingTenant) {
      this.hasLoggedMissingTenant = true;
      this.monitoringService.logCustomEvent("CategoryTagging: tenant not yet populated");
    }
    return tenant?.toLowerCase() === SuperOfficeOwnTenant;
  }

  get isCategoryTaggingEnabled(): boolean {
    return this.outlookService.getRoamingSettingBoolean("EnableCategoryTagging", this.categoryTaggingOnByDefault);
  }

  get enableCategoryOnOpen(): boolean {
    return this.outlookService.getRoamingSettingBoolean("EnableCategoryOnOpen", true);
  }

  get enableCategoryOnSendAndArchive(): boolean {
    return this.outlookService.getRoamingSettingBoolean("EnableCategoryOnSendAndArchive", true);
  }

  get enableCategoryForSharedMailbox(): boolean {
    return this.outlookService.getRoamingSettingBoolean("EnableCategoryForSharedMailbox", true);
  }

  get isCategoryTaggingOnSendAndArchiveEnabled(): boolean {
    return this.isCategoryTaggingFeatureEnabled
      && this.isCategoryTaggingEnabled
      && this.enableCategoryOnSendAndArchive;
  }

  get supportsSavingProjectOnSale(): boolean {
    return this.sharedService.hasCapability(Capability.SupportsSavingProjectOnSale);
  }

  get supportsSmartAlerts(): boolean {
    return this.sharedService.hasCapability(Capability.SofoSmartAlerts);
  }

  get supportsFeatureAnnouncement(): boolean {
    return this.sharedService.hasCapability(Capability.SofoFeatureAnnouncements);
  }

  /* actual preferences we want to use */

  get enableContextualProjectsAndSalesForContact(): boolean {
    return this.getBooleanPreference("EnableContextualProjectsAndSalesForContact");
  }

  saveEnableContextualProjectsAndSalesForContact(value: boolean): Promise<void> {
    return this.saveBooleanPreference("EnableContextualProjectsAndSalesForContact", value);
  }

  get enableDeleteAfterArchive(): boolean {
    return this.getBooleanPreference("DeleteAfterArchive");
  }

  saveEnableDeleteAfterArchive(value: boolean): Promise<void> {
    return this.saveBooleanPreference("DeleteAfterArchive", value);
  }

  // Synchronizer exposes "their" user-state in a user preference
  // Any other value rather than "Active" means we be showing some messages instead of functionality
  get isSynchronizerActiveForThisUser(): boolean {
    return (UserSynchronizerStateValues.Active === this.getStringPreference("DiarySynchronizerState"));
  }

  /* private helper functions */

  getStringPreference(keyName: string): string | null {
    if (!this.isInitialized) {
      throw new Error("Using un-initialized PreferenceService!");
    }

    for ( const {Key, Value} of this.prefs ) {
      if (Key === keyName) {
        return Value;
      }
    }

    return null;
  }

  setStringPreference(keyName: string, value: string): void {
    if (!this.isInitialized) {
      throw new Error("Using un-initialized PreferenceService!");
    }

    for ( const pref of this.prefs ) {
      if (pref.Key === keyName) {
        pref.Value = value;
        return;
      }
    }

    throw new Error("Attempting to set unknown preference!");
  }

  private getBooleanPreference(keyName: string): boolean {
    const value = this.getStringPreference(keyName);

    if (!value) {
      return false;
    }

    const valueLower = value.toLowerCase();
    return valueLower === "1" || valueLower === "true";
  }

  private saveBooleanPreference(keyName: string, value: boolean): Promise<void> {
    const stringValue = value ? "1" : "0";
    return this.savePreference(keyName, stringValue);
  }

  /** internal method only meant to assist unit-testing */
  populatePreferences(prefs: IPreference[]) {
    this.prefs = prefs || [];
    this.isInitialized = true;
  }

  private mapNamedPreferenceToPreference(pref: NamedPreference): IPreference {
    return { Key: pref.Specification.Key, Value: pref.RawValue };
  }
}


export enum UserSynchronizerStateValues {
  Active="Graph_ValidCredentials",
  NoCredentials="Graph_NoCredentials",
  ExpiredCredentials="Graph_ExpiredCredentials"
}
